<template>
  <div
    v-show="isMobile && isMenuOpen"
    class="header-space fixed left-0 top-0 z-menu h-full w-full overflow-hidden bg-gray-100"
  >
    <div class="h-full w-full overflow-y-auto overflow-x-hidden">
      <LayoutMenuMobileHome v-show="currentMobileMenu === null" />
      <KeepAlive>
        <LayoutMenuMobileLocale v-show="currentMobileMenu === 'locale'" />
      </KeepAlive>
      <KeepAlive>
        <LayoutMenuMobileItem
          v-for="item in flattenSolutionsMenu"
          v-show="currentMobileMenu === item.id"
          :key="hash(item)"
          :menu-data="item"
          :type="menuName.SOLUTIONS"
        />
      </KeepAlive>
      <KeepAlive>
        <LayoutMenuMobileItem
          v-for="item in flattenProductsMenu"
          v-show="currentMobileMenu === item.id"
          :key="hash(item)"
          :menu-data="item"
          :type="menuName.PRODUCTS"
        />
      </KeepAlive>
      <KeepAlive>
        <LayoutMenuMobileItem
          v-for="item in flattenResourcesMenu"
          v-show="currentMobileMenu === item.id"
          :key="hash(item)"
          :menu-data="item"
          :type="menuName.RESOURCES"
        />
      </KeepAlive>
      <KeepAlive>
        <LayoutMenuMobileItem
          v-for="item in flattenTopicsMenu"
          v-show="currentMobileMenu === item.id"
          :key="hash(item)"
          :menu-data="item"
          :type="menuName.TOPICS"
        />
      </KeepAlive>
      <KeepAlive>
        <LayoutMenuMobileItem
          v-for="item in flattenEcosystemMenu"
          v-show="currentMobileMenu === item.id"
          :key="hash(item)"
          :menu-data="item"
          :type="menuName.ECOSYSTEM"
        />
      </KeepAlive>
    </div>
  </div>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import type {
    IFlattenItem,
    IFlattenParent,
  } from '@/components/Layout/Menu/Mobile/types';
  import type { IFormatMenu } from '@/stores/menuStore';

  const menuStore = useMenuStore();

  const {
    menuName,
    isMenuOpen,
    currentMobileMenu,
    formatSolutionsMenu,
    formatProductGroupMenu,
    formatResourcesMenu,
    formatTopicsMenu,
    formatEcosystemMenu,
  } = storeToRefs(menuStore);

  const { isMobile } = useScreen();

  const flattenSolutionsMenu = computed((): IFlattenItem[] =>
    setFlattenMenus(menuName.value.SOLUTIONS, formatSolutionsMenu.value)
  );

  const flattenProductsMenu = computed((): IFlattenItem[] =>
    setFlattenMenus(menuName.value.PRODUCTS, formatProductGroupMenu.value)
  );

  const flattenResourcesMenu = computed((): IFlattenItem[] =>
    setFlattenMenus(menuName.value.RESOURCES, formatResourcesMenu.value)
  );

  const flattenTopicsMenu = computed((): IFlattenItem[] =>
    setFlattenMenus(menuName.value.TOPICS, formatTopicsMenu.value)
  );

  const flattenEcosystemMenu = computed((): IFlattenItem[] =>
    setFlattenMenus(menuName.value.ECOSYSTEM, formatEcosystemMenu.value)
  );

  const setFlattenMenus = (
    typeName: string,
    menus: IFormatMenu[]
  ): IFlattenItem[] => {
    const result: IFlattenItem[] = [];

    const mapSubMenus = (
      parentId: string,
      subMenus: IFormatMenu[],
      level: number
    ): IFlattenItem['subMenus'] =>
      subMenus.map((subMenu) => ({
        id: `${parentId}_${level}-${subMenu.name}`,
        level,
        name: subMenu.name,
        type: subMenu.type,
        shortIntroduction: subMenu.shortIntroduction,
        link: subMenu.link,
        isHot: subMenu?.isHot || false,
        isNew: subMenu?.isNew || false,
        isSuite: subMenu?.isSuite || false,
        hasSubMenu: subMenu.subMenus.length > 0,
      }));

    result.push({
      code: undefined,
      id: typeName,
      level: 0,
      name: typeName,
      type: 1,
      link: null,
      subMenus: mapSubMenus(typeName, menus, 1),
      parent: { id: '', level: 0, name: 'Back' },
    });

    const flattenMenusRecursive = (
      menuList: IFormatMenu[],
      level: number,
      parent: IFlattenParent
    ): void => {
      menuList.forEach((menu) => {
        const currentId = `${parent.id}_${level}-${menu.name}`;
        const childLevel = level + 1;

        result.push({
          code: menu.code,
          id: currentId,
          level,
          name: menu.name,
          type: menu.type,
          link: menu.link,
          subMenus: mapSubMenus(currentId, menu.subMenus, childLevel),
          parent,
        });

        if (menu.subMenus.length > 0) {
          flattenMenusRecursive(menu.subMenus, childLevel, {
            id: currentId,
            level,
            name: menu.name,
          });
        }
      });
    };

    flattenMenusRecursive(menus, 1, { id: typeName, level: 0, name: typeName });

    return result;
  };
</script>

<style lang="scss" scoped>
  .header-space {
    @apply pt-header-mobile-height tablet:pt-header-height;
  }
</style>
